import { createAsyncThunk } from "@reduxjs/toolkit"
import { fetchBackend } from "@/core/apis/backend"

//Phase 2 (2026-09-11) — questionAid here is actually a bank_id now (BenchmarkEntry.canonicalAid),
//hits the bank_id-dedicated distribution route rather than the legacy question_aid one.
export const fetchBenchmarkQuestionDistribution = createAsyncThunk(
  "benchmarkQuestion/fetchDistribution",
  async ({ questionAid, sector }: { questionAid: string; sector?: string | null }) => {
    const params = new URLSearchParams()
    if (sector && sector !== "all") params.set("sector", sector)
    const qs = params.toString()
    return fetchBackend({ url: `/benchmark/question-bank/${questionAid}/distribution${qs ? "?" + qs : ""}` })
  }
)

export interface BenchmarkQuestionCalculateProgress {
  current: number
  total: number
  label: string | null
}

export const fetchBenchmarkCalculateStatus = createAsyncThunk(
  "benchmarkQuestion/calculateStatus",
  async () => {
    return fetchBackend({ url: "/supervisor/benchmark/calculate/status" }) as Promise<{
      calculating: boolean
      calculatingQuestions: boolean
      calculatingGeneral: boolean
      lastQuestionCalculateAt: number | null
      questionCalculateProgress: BenchmarkQuestionCalculateProgress | null
    }>
  }
)

export const calculateBenchmarkQuestions = createAsyncThunk(
  "benchmarkQuestion/calculate",
  async (params: import("./benchmarkQuestion.types").CalcQuestionParams = {}) => {
    return fetchBackend({ url: "/supervisor/benchmark/calculate/questions", method: "POST", data: params })
  }
)

//eNPS (benchmark.application.ts calculate() -> calculateNpsViaBank(); the note/rps/participation
//types it also rebuilt were removed 2026-09-21, nothing read them). Fired alongside
//calculateBenchmarkQuestions from the page-level "Calculer" button (2026-09-14, Tom: "on peut
//faire les 2 en 1") — independent flags/rows server-side (isCalculating vs
//isCalculatingQuestions, type='nps' vs type='question'), safe to run concurrently. getCalculateStatus() already reports `calculating` as the OR of both,
//so the existing poll loop needs no change to wait for both to finish.
export const calculateBenchmarkGeneral = createAsyncThunk(
  "benchmarkQuestion/calculateGeneral",
  async () => {
    return fetchBackend({ url: "/supervisor/benchmark/calculate", method: "POST" })
  }
)

export interface TemplateBenchmarkStatus {
  //Real owning account of the template — null for an IDTree master/library template, only used
  //client-side to target /supervisor/benchmark/account when a raw supervisor sets its sector
  accountId: string | null
  sector: string | null
  total: number
  withBenchmark: number
  withoutBenchmark: number
  byAxis: {
    axis: string
    total: number
    withBenchmark: number
    questions: { questionId: string; topic: string | null; hasBenchmark: boolean }[]
  }[]
  //Supervisor-only convenience — raw text of questions with no benchmark match yet
  unmatched: { questionId: string; text: string; axisId: string | null; topicId: string }[]
  supervisorQuestions: {
    questionId: string
    text: string
    axisId: string | null
    topicId: string
    bankId: string | null
    hasBenchmark: boolean
  }[]
  //Choice/open questions — no benchmark notion at all, never counted in total/withBenchmark
  noBenchmarkQuestions: { questionId: string; text: string; axisId: string | null; topicId: string }[]
}

//asSupervisor = raw supervisor editing a template directly from /supervisor/templates (no client
//impersonation, so no accountId on this JWT at all — the template's own account is resolved server-side)
export const fetchTemplateBenchmarkStatus = createAsyncThunk(
  "benchmarkQuestion/fetchTemplateStatus",
  async ({ templateId, asSupervisor }: { templateId: string; asSupervisor?: boolean }) => {
    const res = await fetchBackend({ url: `${asSupervisor ? "/supervisor" : ""}/benchmark/template/${templateId}/status` })
    if (res.error) return null
    return res as TemplateBenchmarkStatus
  }
)

export const updateAccountSector = createAsyncThunk(
  "benchmarkQuestion/updateAccountSector",
  async (sector: string | null) => {
    const res = await fetchBackend({ url: "/update-sector", method: "POST", data: { sector } })
    return { sector, error: !!res.error }
  }
)

export const updateAccountSector_AsSupervisor = createAsyncThunk(
  "benchmarkQuestion/updateAccountSectorAsSupervisor",
  async ({ accountId, sector }: { accountId: string; sector: string | null }) => {
    const res = await fetchBackend({
      url: "/supervisor/update-sector",
      method: "POST",
      data: { accountId, sector }
    })
    return { sector, error: !!res.error }
  }
)

